import { useEffect, useState } from "react";
import {
  Button,
  Heading,
  Text,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalOverlay,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
} from "@chakra-ui/react";
import API from "../api";

type Collaborators = {
  email: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  ownerEmail?: string;
  collaborators?: Collaborators[];
  role: "owner" | "collaborator";
  refetchScene?: () => void;
};

const ShareSceneDialog = ({
  isOpen,
  onClose,
  sceneId,
  collaborators,
  ownerEmail,
  role,
  refetchScene,
}: Props) => {
  const [email, setEmail] = useState("");
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const shareScene = () => {
    setErrorMessage("");
    API.shareScene(sceneId, email)
      .then(() => {
        setSuccess(true);
        setEmail("");
        refetchScene && refetchScene();
      })
      .catch((error) => {
        setSuccess(false);
        setErrorMessage(error.message);
      });
  };

  useEffect(() => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setEmailIsValid(regex.test(email));
  }, [email]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setSuccess(false);
        setErrorMessage("");
        onClose();
      }}
      size="2xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="md">
            {role === "owner" ? "Share Scene" : "Shared Scene"}
          </Heading>
          <Text fontSize="sm" fontWeight="normal" opacity="0.5">
            {role === "owner"
              ? "Invite other users to start collaborating on inference validation"
              : "This scene is shared with you."}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {success && (
            <Alert status="success" mb={5}>
              <AlertIcon />
              <AlertTitle>Scene shared successfully.</AlertTitle>
            </Alert>
          )}
          {role === "owner" && (
            <>
              <FormControl>
                <FormLabel>Invite Users</FormLabel>
                <Flex gap="4">
                  <Input
                    size="sm"
                    placeholder="username@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    onClick={() => shareScene()}
                    isDisabled={!emailIsValid}
                  >
                    Send Invite
                  </Button>
                </Flex>
                <FormHelperText>
                  Only users with a WhalesAI account may be invited to view and
                  edit scenes. Any user invited to a scene may validate
                  inferences, download inferences, and view imagery. By sharing
                  this scene, you confirm that the recipient is covered under
                  the imagery licensing agreement.
                </FormHelperText>
              </FormControl>
              {errorMessage && (
                <Alert status="error" mt={4}>
                  <AlertIcon />
                  <AlertTitle>{errorMessage}</AlertTitle>
                </Alert>
              )}
              <Divider my={4} />
            </>
          )}
          <Heading size="sm" mb={5}>
            Scene Collaborators
          </Heading>
          {!collaborators ||
            (collaborators.length === 0 && (
              <Text fontSize="sm" fontWeight="normal" opacity="0.5" mb={4}>
                This scene is not shared with any user yet.
              </Text>
            ))}
          {role === "owner" && (
            <Flex
              my="2"
              alignItems="center"
              justifyContent="space-between"
            >
              <Flex fontSize="sm" flexDir="column">
                <Text fontWeight="bold" textTransform="capitalize">
                  You (Owner)
                </Text>
              </Flex>
              <Badge
                colorScheme="blue"
                variant="subtle"
                fontSize="10px"
                letterSpacing="0.5px"
                fontWeight="regular"
              >
                Owner
              </Badge>
            </Flex>
          )}
          {collaborators?.map((user) => (
            <Flex
              my="2"
              alignItems="center"
              justifyContent="space-between"
              key={user.email}
            >
              <Flex fontSize="sm" flexDir="column">
                <Text fontWeight="bold" textTransform="capitalize">
                  {user.email.split("@")[0]}
                </Text>
                <Text opacity="0.8">{user.email}</Text>
              </Flex>
              <Badge
                colorScheme="green"
                variant="subtle"
                fontSize="10px"
                letterSpacing="0.5px"
                fontWeight="regular"
              >
                Active
              </Badge>
            </Flex>
          ))}
          {ownerEmail && (
            <>
              <Flex my="2" alignItems="center" justifyContent="space-between">
                <Flex fontSize="sm" flexDir="column">
                  <Text fontWeight="bold" textTransform="capitalize">
                    {ownerEmail.split("@")[0]}
                  </Text>
                  <Text opacity="0.8">{ownerEmail}</Text>
                </Flex>
                <Badge
                  colorScheme="primary"
                  variant="solid"
                  fontSize="10px"
                  letterSpacing="0.5px"
                  fontWeight="regular"
                >
                  Owner
                </Badge>
              </Flex>
              <Text fontSize="sm" my="4">
                This scene was shared with you by the owner. You can view and
                validate inferences, download inferences, and view imagery. You
                cannot share this scene with other users, or delete the scene.
              </Text>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShareSceneDialog;
